import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Printer, X } from 'lucide-react';
import { formatRial } from '@/lib/formatMoney';
import { getAccountingPrintSettings } from '@/lib/printSettings';
import {
  releaseProjectLedgerVoucherPrintMemory,
  takeProjectLedgerVoucherPayloadForPage,
  type ProjectLedgerVoucherPayload,
} from '@/lib/projectLedgerVoucherPrint';

const ProjectLedgerVoucherPrintPage: React.FC = () => {
  const [payload] = React.useState<ProjectLedgerVoucherPayload | null>(() =>
    takeProjectLedgerVoucherPayloadForPage(),
  );
  const settings = getAccountingPrintSettings();

  React.useEffect(() => {
    return () => releaseProjectLedgerVoucherPrintMemory();
  }, []);

  if (!payload) {
    return <Navigate to="/projects" replace />;
  }

  const isDeposit = payload.kind === 'deposit';
  const backHref = `/projects/${payload.project.id}/overview`;

  const infoCells: Array<{ label: string; value: string }> = [
    { label: 'پروژه', value: payload.project.label || '—' },
    { label: 'تاریخ', value: payload.dateFa || '—' },
    { label: 'نوع', value: payload.expenseTypeTitle || payload.kindLabel || '—' },
    { label: 'طرف حساب', value: payload.beneficiary || '—' },
    { label: 'سند', value: payload.docNo || '—' },
    { label: 'روش', value: payload.paymentMethod || '—' },
  ];

  return (
    <div className="min-h-screen bg-slate-200 print:min-h-0 print:bg-white" dir="rtl">
      <style>{`
        @media print {
          @page { size: A5 portrait; margin: 6mm; }
          .no-print { display: none !important; }
          html, body, #root {
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
            background: #fff !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>

      <div className="no-print sticky top-0 z-50 border-b border-slate-300 bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-md flex-wrap items-center justify-between gap-2">
          <div className="text-sm">
            <div className="font-semibold text-slate-800">پیش‌نمایش فیش</div>
            <div className="text-xs text-slate-500">{payload.voucherNo}</div>
          </div>
          <div className="flex gap-2">
            <Link
              to={backHref}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50"
            >
              <X className="h-3.5 w-3.5" />
              بستن
            </Link>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs text-white hover:bg-emerald-700"
            >
              <Printer className="h-3.5 w-3.5" />
              چاپ
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto my-4 w-[105mm] max-w-[calc(100vw-1.5rem)] print:my-0 print:w-full print:max-w-none">
        <article className="overflow-hidden rounded border border-slate-800 bg-white text-[11px] leading-snug text-slate-900 shadow-sm print:rounded-none print:border print:shadow-none">
          {/* هدر فشرده */}
          <header className="flex items-center gap-2 border-b border-slate-800 px-2.5 py-2">
            {settings.logoDataUrl ? (
              <img src={settings.logoDataUrl} alt="" className="h-8 w-8 shrink-0 object-contain" />
            ) : (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-slate-400 text-[8px] text-slate-500">
                لوگو
              </div>
            )}
            <div className="min-w-0 flex-1 text-center">
              <div className="truncate text-[12px] font-bold">{settings.companyName}</div>
              <div className="mt-0.5 text-[10px] font-semibold tracking-wide">
                فیش {payload.kindLabel}
              </div>
            </div>
            <div className="shrink-0 text-left text-[9px] text-slate-600" dir="ltr">
              <div>{payload.dateFa || '—'}</div>
              <div className="mt-0.5 font-mono text-[8px] opacity-80">{payload.voucherNo}</div>
            </div>
          </header>

          {/* مبلغ */}
          <div className="border-b border-slate-800 px-2.5 py-2 text-center">
            <div className={`font-mono text-[16px] font-bold ${isDeposit ? 'text-emerald-800' : 'text-rose-800'}`}>
              {formatRial(payload.amount)}
              <span className="mr-1 text-[10px] font-normal text-slate-600">ریال</span>
            </div>
            {payload.amountInWords ? (
              <div className="mt-0.5 text-[9px] leading-relaxed text-slate-600">{payload.amountInWords}</div>
            ) : null}
          </div>

          {/* مشخصات دو ستونه */}
          <div className="grid grid-cols-2 border-b border-slate-800">
            {infoCells.map((cell, index) => (
              <div
                key={cell.label}
                className={`px-2 py-1.5 ${index % 2 === 0 ? 'border-l border-slate-300' : ''} ${
                  index < 4 ? 'border-b border-slate-300' : ''
                }`}
              >
                <div className="text-[8px] text-slate-500">{cell.label}</div>
                <div className="mt-0.5 break-words font-medium">{cell.value}</div>
              </div>
            ))}
          </div>

          {/* شرح */}
          <div className="border-b border-slate-800 px-2.5 py-1.5">
            <div className="text-[8px] text-slate-500">شرح</div>
            <div className="mt-0.5 break-words">{payload.description || '—'}</div>
          </div>

          {/* امضاها */}
          <div className="grid grid-cols-3 gap-2 px-2.5 py-3 text-center text-[9px] text-slate-600">
            <div>
              <div className="mb-6 border-b border-dashed border-slate-400" />
              تهیه‌کننده
            </div>
            <div>
              <div className="mb-6 border-b border-dashed border-slate-400" />
              تأیید
            </div>
            <div>
              <div className="mb-6 border-b border-dashed border-slate-400" />
              دریافت
            </div>
          </div>

          <footer className="border-t border-slate-300 px-2.5 py-1 text-center text-[8px] text-slate-500">
            {settings.companyName}
            {payload.sourceLabel ? ` · ${payload.sourceLabel}` : ''}
          </footer>
        </article>
      </div>
    </div>
  );
};

export default ProjectLedgerVoucherPrintPage;
